// frontend/src/components/customers/Customers.js
import React, { useState, useEffect, useCallback } from 'react';
import * as api from '../../api';
import { fmt, Pager } from '../common/CommonComponents';

function CustomerProfile({ customerId, onBack, onChanged, showAlert, canEdit }) {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [account, setAccount] = useState(null);

  const load = useCallback(() => {
    Promise.all([api.getCustomerHistory(customerId), api.getCustomerAccount(customerId)])
      .then(([historyData, accountData]) => { setData(historyData); setAccount(accountData); })
      .catch(e => showAlert(e.message, "error"));
  }, [customerId, showAlert]);

  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="spinner">Loading…</div>;
  const { customer, history } = data;

  const startEdit = () => {
    setForm({ name: customer.name, phone: customer.phone || "", email: customer.email || "", address: customer.address || "", notes: customer.notes || "", tags: (customer.tags || []).join(", ") });
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.updateCustomer(customerId, { ...form, tags: form.tags.split(",").map(t => t.trim()).filter(Boolean) });
      showAlert("Customer updated");
      setEditing(false);
      load();
      onChanged && onChanged();
    } catch (e) { showAlert(e.message, "error"); }
    setSaving(false);
  };

  const archive = async () => {
    if (!window.confirm(`Archive ${customer.name}? They'll no longer show up in search, but their order/sale history is kept.`)) return;
    try {
      await api.archiveCustomer(customerId);
      showAlert("Customer archived");
      onChanged && onChanged();
      onBack();
    } catch (e) { showAlert(e.message, "error"); }
  };

  return (
    <div>
      <button className="btn btn-secondary" style={{ fontSize: 12, marginBottom: 16 }} onClick={onBack}>← Back to Customers</button>

      <div className="card" style={{ marginBottom: 20 }}>
        <div className="section-header">
          <div>
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>{customer.name}</h2>
            {customer.tags?.length > 0 && (
              <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                {customer.tags.map(t => <span key={t} style={{ fontSize: 10, fontWeight: 700, background: "#fff0f2", color: "#e94560", padding: "2px 8px", borderRadius: 4 }}>{t}</span>)}
              </div>
            )}
          </div>
          {canEdit && !editing && (
            <div style={{ display: "flex", gap: 6 }}>
              <button className="btn-sm btn-edit" onClick={startEdit}>Edit</button>
              <button className="btn-sm btn-reject" onClick={archive}>Archive</button>
            </div>
          )}
        </div>

        {editing ? (
          <div style={{ marginTop: 12 }}>
            <div className="form-row">
              <div className="form-group"><label>Name *</label><input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} /></div>
              <div className="form-group"><label>Phone</label><input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} /></div>
            </div>
            <div className="form-row">
              <div className="form-group"><label>Email</label><input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} /></div>
              <div className="form-group"><label>Tags (comma-separated)</label><input value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))} placeholder="VIP, Wholesale" /></div>
            </div>
            <div className="form-group"><label>Address</label><input value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} /></div>
            <div className="form-group"><label>Notes</label><textarea rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} /></div>
            <div style={{ display: "flex", gap: 10 }}>
              <button className="btn btn-primary" disabled={saving} onClick={save}>{saving ? "Saving…" : "Save"}</button>
              <button className="btn btn-secondary" onClick={() => setEditing(false)}>Cancel</button>
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 24, marginTop: 12, fontSize: 13 }}>
            <div><span style={{ color: "var(--text-3)" }}>Phone:</span> {customer.phone || "—"}</div>
            <div><span style={{ color: "var(--text-3)" }}>Email:</span> {customer.email || "—"}</div>
            <div><span style={{ color: "var(--text-3)" }}>Address:</span> {customer.address || "—"}</div>
            {customer.notes && <div style={{ width: "100%", color: "var(--text-2)" }}>📝 {customer.notes}</div>}
          </div>
        )}
      </div>

      <div style={{ display: "flex", gap: 16, marginBottom: 20, flexWrap: "wrap" }}>
        <div className="card" style={{ flex: 1, minWidth: 160, textAlign: "center", padding: 16 }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: "#10B981" }}>{fmt(customer.lifetimeValue)}</div>
          <div style={{ fontSize: 11, color: "var(--text-3)" }}>Lifetime Value</div>
        </div>
        <div className="card" style={{ flex: 1, minWidth: 160, textAlign: "center", padding: 16 }}>
          <div style={{ fontSize: 20, fontWeight: 800 }}>{customer.totalOrders}</div>
          <div style={{ fontSize: 11, color: "var(--text-3)" }}>Orders</div>
        </div>
        <div className="card" style={{ flex: 1, minWidth: 160, textAlign: "center", padding: 16 }}>
          <div style={{ fontSize: 20, fontWeight: 800 }}>{customer.totalSales}</div>
          <div style={{ fontSize: 11, color: "var(--text-3)" }}>Sales</div>
        </div>
        <div className="card" style={{ flex: 1, minWidth: 160, textAlign: "center", padding: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700 }}>{customer.lastActivityAt ? new Date(customer.lastActivityAt).toLocaleDateString() : "—"}</div>
          <div style={{ fontSize: 11, color: "var(--text-3)" }}>Last Activity</div>
        </div>
      </div>

      {account && <div className="card" style={{ marginBottom: 20 }}>
        <div className="section-header"><div><h3 style={{ margin: 0, fontSize: 14, fontWeight: 800 }}>Customer Account</h3><p style={{ fontSize: 11.5, color: "var(--text-3)", marginTop: 4 }}>Cash, outstanding balances, refundable customer-held funds and credit movements</p></div></div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 12, marginBottom: 16 }}>
          {[
            ["Cash Received",account.cashReceived],
            ["Cash Refunded",account.cashRefunded],
            ["Sales Value",account.salesValue],
            ["Outstanding Balance",account.outstandingBalance],
            ["Customer-Held Funds",account.customerHeldFunds],
          ].map(([label,value]) => <div key={label} style={{ padding: 12, border: "1px solid var(--border)", borderRadius: 8 }}><div style={{ fontSize: 11, color: "var(--text-3)" }}>{label}</div><div style={{ fontSize: 18, fontWeight: 800, marginTop: 4 }}>{fmt(value || 0)}</div></div>)}
        </div>
        <div className="table-wrap" style={{ marginBottom: 16 }}><table className="w-full"><thead><tr><th>Order</th><th>Sales</th><th>Paid</th><th>Outstanding</th><th>Held Funds</th><th>Status</th></tr></thead><tbody>
          {(account.orders || []).length === 0 ? <tr><td colSpan={6} style={{ textAlign:"center", color:"var(--text-3)", padding:20 }}>No order account activity yet.</td></tr> :
            account.orders.map(o => <tr key={o.order_id}><td><code>{o.order_id}</code></td><td>{fmt(o.total_sales)}</td><td>{fmt(o.total_paid)}</td><td>{fmt(o.balance)}</td><td>{fmt(o.refundable)}</td><td>{o.payment_status || o.order_status}</td></tr>)}
        </tbody></table></div>
        <h4 style={{ margin:"0 0 8px", fontSize:13 }}>Account Movements</h4>
        <div className="table-wrap"><table className="w-full"><thead><tr><th>Date</th><th>Type</th><th>Reference</th><th>Description</th><th>Amount</th></tr></thead><tbody>
          {(account.movements || []).length === 0 ? <tr><td colSpan={5} style={{ textAlign:"center", color:"var(--text-3)", padding:20 }}>No account movements yet.</td></tr> :
            account.movements.map((m,i) => <tr key={`${m.type}-${m.reference || i}-${m.created_at || i}`}><td>{m.created_at ? new Date(m.created_at).toLocaleDateString() : "—"}</td><td>{String(m.type || "").replace(/_/g," ")}</td><td><code>{m.reference || "—"}</code></td><td>{m.description || "—"}</td><td>{fmt(Math.abs(Number(m.amount || 0)))}</td></tr>)}
        </tbody></table></div>
      </div>}

      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        <div style={{ padding: 16, borderBottom: "1px solid var(--border)" }}>
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>Order & Sale History</h3>
        </div>
        <div className="table-wrap">
          <table className="w-full">
            <thead><tr><th>Type</th><th>ID</th><th>Details</th><th>Amount</th><th>Status</th><th>Date</th></tr></thead>
            <tbody>
              {history.length === 0 ? (
                <tr><td colSpan={6} style={{ textAlign: "center", color: "var(--text-3)", padding: 32 }}>No activity yet</td></tr>
              ) : history.map(h => (
                <tr key={`${h.type}-${h.id}`} style={h.cancelled ? { background: 'rgba(239, 68, 68, .055)' } : undefined}>
                  <td>{h.type === "order" ? "📦 Order" : "🛒 Sale"}</td>
                  <td style={{ fontFamily: "monospace", fontSize: 11 }}>{h.id}</td>
                  <td>
                    <div>{h.label}</div>
                    {h.cancelled && <div style={{ color: '#B91C1C', fontSize: 11, fontWeight: 700, marginTop: 3 }}>Cancelled{h.cancelReason ? `: ${h.cancelReason}` : ''}</div>}
                  </td>
                  <td style={{ fontWeight: 700, color: h.cancelled ? '#B91C1C' : '#10B981' }}>{fmt(h.amount)}</td>
                  <td>
                    {h.cancelled ? (
                      <div>
                        <span className="badge badge-cancelled">Cancelled</span>
                        <div style={{ color: 'var(--text-3)', fontSize: 10.5, marginTop: 3 }}>{h.paymentStatus} before cancellation</div>
                      </div>
                    ) : h.status}
                  </td>
                  <td>{new Date(h.date).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── Bulk WhatsApp (Tier 20 — customer WhatsApp suite, Growth+ plan) ──
// From the Clients page: pick a group (e.g. "everyone with an
// outstanding balance"), write one message, get a queue of ready-to-
// send links — one tap per client. Nothing sends automatically (see
// utils/whatsapp.js's header comment on the backend for why); this
// modal's own "Sent ✓" state is purely local UI tracking of what's
// been tapped, not a real delivery receipt.
const MESSAGE_TEMPLATES = [
  { label: "Balance reminder", text: "Hi {{name}}, this is a friendly reminder that you have an outstanding balance with us. Please reach out if you'd like to settle it. Thank you!" },
  { label: "New arrivals", text: "Hi {{name}}, we've just added some new items you might like! Come check them out." },
  { label: "Thank you", text: "Hi {{name}}, thank you for being a valued customer — we appreciate your support!" },
];

function BulkWhatsappModal({ onClose, showAlert }) {
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [filter, setFilter] = useState("all"); // all | balance | active
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [message, setMessage] = useState("");
  const [generating, setGenerating] = useState(false);
  const [queue, setQueue] = useState(null); // { links, skipped } | null
  const [sentIds, setSentIds] = useState(new Set());

  useEffect(() => {
    api.getWhatsappOverview()
      .then(r => setCustomers(r.customers || []))
      .catch(e => showAlert(e.message, "error"))
      .finally(() => setLoading(false));
  }, [showAlert]);

  const filtered = customers.filter(c => {
    if (filter === "balance" && !(c.outstandingBalance > 0)) return false;
    if (filter === "active" && !(c.activeOrderCount > 0)) return false;
    if (search && !c.name?.toLowerCase().includes(search.toLowerCase()) && !c.phone?.includes(search)) return false;
    return true;
  });

  const toggleOne = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map(c => c.customerId)));
  };

  const generate = async () => {
    if (selected.size === 0) { showAlert("Select at least one customer", "error"); return; }
    if (!message.trim()) { showAlert("Write a message first", "error"); return; }
    setGenerating(true);
    try {
      const r = await api.getBulkWhatsappLinks([...selected], message);
      setQueue(r);
      setSentIds(new Set());
    } catch(e) { showAlert(e.message, "error"); }
    setGenerating(false);
  };

  const markSent = (customerId, link) => {
    window.open(link, "_blank", "noreferrer");
    setSentIds(prev => new Set(prev).add(customerId));
  };

  return (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: 16 }}>
      <div className="card" style={{ maxWidth: 560, width: "100%", maxHeight: "88vh", overflowY: "auto" }}>
        <div className="section-header">
          <h2 style={{ margin: 0, fontSize: 15 }}>📲 Bulk WhatsApp</h2>
          <button className="btn-sm btn-secondary" onClick={onClose}>✕ Close</button>
        </div>

        {loading ? (
          <div className="spinner">Loading customers...</div>
        ) : !queue ? (
          <>
            <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
              {[["all", "All"], ["balance", "Has balance due"], ["active", "Has active order"]].map(([k, label]) => (
                <button key={k} className={filter === k ? "btn-sm btn-primary" : "btn-sm btn-secondary"} onClick={() => setFilter(k)}>{label}</button>
              ))}
            </div>
            <input placeholder="🔍 Search by name or phone..." value={search} onChange={e => setSearch(e.target.value)} style={{ marginBottom: 10 }} />

            <div style={{ maxHeight: 220, overflowY: "auto", border: "1px solid var(--border)", borderRadius: 8, marginBottom: 14 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", borderBottom: "1px solid var(--border)", fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>
                <input type="checkbox" checked={filtered.length > 0 && selected.size === filtered.length} onChange={toggleAll} />
                Select all ({filtered.length})
              </label>
              {filtered.length === 0 ? (
                <div style={{ padding: 16, textAlign: "center", color: "var(--text-3)", fontSize: 12.5 }}>No customers match this filter.</div>
              ) : filtered.map(c => (
                <label key={c.customerId} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", borderBottom: "1px solid var(--border)", fontSize: 12.5, cursor: "pointer" }}>
                  <input type="checkbox" checked={selected.has(c.customerId)} onChange={() => toggleOne(c.customerId)} />
                  <span style={{ flex: 1 }}>{c.name} <span style={{ color: "var(--text-3)" }}>({c.phone})</span></span>
                  {c.outstandingBalance > 0 && <span style={{ color: "#EF4444", fontWeight: 700 }}>{fmt(c.outstandingBalance)} due</span>}
                </label>
              ))}
            </div>

            <div style={{ marginBottom: 8 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: "var(--text-2)", marginBottom: 6, display: "block" }}>Message template</label>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
                {MESSAGE_TEMPLATES.map(t => (
                  <button key={t.label} className="btn-sm btn-secondary" style={{ fontSize: 11 }} onClick={() => setMessage(t.text)}>{t.label}</button>
                ))}
              </div>
              <textarea rows={3} value={message} onChange={e => setMessage(e.target.value)} placeholder="Write your message... use {{name}} to personalize" />
            </div>

            <button className="btn btn-primary" disabled={generating || selected.size === 0} onClick={generate} style={{ width: "100%" }}>
              {generating ? "Generating…" : `Generate ${selected.size} Link${selected.size === 1 ? "" : "s"}`}
            </button>
          </>
        ) : (
          <>
            <p style={{ fontSize: 12.5, color: "var(--text-3)", marginBottom: 12 }}>
              Tap "Send" to open WhatsApp for each customer — you still have to tap Send inside WhatsApp yourself. Nothing is sent automatically.
            </p>
            <div style={{ maxHeight: 320, overflowY: "auto", marginBottom: 12 }}>
              {queue.links.map(l => (
                <div key={l.customerId} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 10px", borderBottom: "1px solid var(--border)", fontSize: 12.5 }}>
                  <span>{l.name}</span>
                  {sentIds.has(l.customerId) ? (
                    <span style={{ color: "#10B981", fontWeight: 700 }}>Sent ✓</span>
                  ) : (
                    <button className="btn-sm btn-primary" onClick={() => markSent(l.customerId, l.link)}>Send</button>
                  )}
                </div>
              ))}
              {queue.skipped.length > 0 && (
                <div style={{ marginTop: 10, fontSize: 11.5, color: "var(--text-3)" }}>
                  Skipped ({queue.skipped.length}, no phone on file): {queue.skipped.map(s => s.name).join(", ")}
                </div>
              )}
            </div>
            <button className="btn btn-secondary" style={{ width: "100%" }} onClick={() => { setQueue(null); setSelected(new Set()); }}>← Back to selection</button>
          </>
        )}
      </div>
    </div>
  );
}

function Customers({ showAlert, user }) {
  const blank = { name: "", phone: "", email: "", address: "", notes: "", tags: "" };
  const [form, setForm] = useState(blank);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [upgradeInfo, setUpgradeInfo] = useState(null);
  const [showBulkWhatsapp, setShowBulkWhatsapp] = useState(false);

  const canEdit = ["manager", "sales"].includes(user.role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.getCustomers({ search, page, limit: 20 });
      setCustomers(r.customers);
      setTotal(r.total);
    } catch (e) {
      if (e.status === 403 && e.data?.upgradeRequired) {
        setUpgradeInfo(e.data);
      } else {
        showAlert("Failed to load customers", "error");
      }
    }
    setLoading(false);
  }, [search, page, showAlert]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.name.trim()) { showAlert("Name is required", "error"); return; }
    setSubmitting(true);
    try {
      await api.createCustomer({ ...form, tags: form.tags.split(",").map(t => t.trim()).filter(Boolean) });
      showAlert("Customer added!");
      setForm(blank);
      setShowForm(false);
      load();
    } catch (e) { showAlert(e.message, "error"); }
    setSubmitting(false);
  };

  if (selectedId) {
    return <CustomerProfile customerId={selectedId} onBack={() => setSelectedId(null)} onChanged={load} showAlert={showAlert} canEdit={canEdit} />;
  }

  if (upgradeInfo) {
    return (
      <div className="card" style={{ textAlign: "center", padding: 48 }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>👤</div>
        <h2 style={{ margin: "0 0 8px" }}>Customers is a Growth+ feature</h2>
        <p style={{ color: "var(--text-3)", maxWidth: 420, margin: "0 auto 20px" }}>{upgradeInfo.error}</p>
        {user.role === "manager" && (
          <button className="btn btn-primary" onClick={() => window.location.hash = "plan"}>View Plans</button>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="section-header">
        <div>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>👤 Customers</h2>
          <p style={{ fontSize: 12, color: "var(--text-3)", marginTop: 4 }}>Your customer records, linked to orders and sales</p>
        </div>
        {canEdit && <button className="btn btn-primary" onClick={() => setShowForm(true)}>+ New Customer</button>}
      </div>

      {canEdit && user.planFeatures?.hasWhatsappTools !== false && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
          <button className="btn-sm btn-edit" onClick={() => setShowBulkWhatsapp(true)}>📲 Bulk WhatsApp</button>
        </div>
      )}

      {showBulkWhatsapp && (
        <BulkWhatsappModal onClose={() => setShowBulkWhatsapp(false)} showAlert={showAlert} />
      )}

      {showForm && (
        <div className="card" style={{ marginBottom: 20, border: "2px solid var(--accent)" }}>
          <div className="section-header">
            <h2 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>➕ Add New Customer</h2>
            <button className="btn btn-secondary" style={{ fontSize: 12 }} onClick={() => { setShowForm(false); setForm(blank); }}>✕ Cancel</button>
          </div>
          <div className="form-row">
            <div className="form-group"><label>Name *</label><input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Customer full name" /></div>
            <div className="form-group"><label>Phone</label><input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="080..." /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label>Email</label><input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} /></div>
            <div className="form-group"><label>Tags (comma-separated)</label><input value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))} placeholder="VIP, Wholesale" /></div>
          </div>
          <div className="form-group"><label>Address</label><input value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} /></div>
          <div className="form-group"><label>Notes</label><textarea rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} /></div>
          <button className="btn btn-primary" disabled={submitting} onClick={submit}>{submitting ? "Adding…" : "Add Customer"}</button>
        </div>
      )}

      <div className="card" style={{ padding: 16, marginBottom: 0 }}>
        <div className="search-bar">
          <input placeholder="🔍 Search by name, phone or email..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
        </div>
        {canEdit && <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}><button className="btn-sm btn-edit" onClick={api.exportCustomers}>⬇ Export CSV</button></div>}
      </div>

      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        {loading ? <div className="spinner">Loading customers...</div> : (
          <div className="table-wrap">
            <table className="w-full">
              <thead><tr><th>Name</th><th>Phone</th><th>Email</th><th>Orders</th><th>Sales</th><th>Lifetime Value</th><th>Last Activity</th></tr></thead>
              <tbody>
                {customers.length === 0 ? (
                  <tr><td colSpan={7} style={{ textAlign: "center", color: "var(--text-3)", padding: 48 }}>
                    <div style={{ fontSize: 48, marginBottom: 12 }}>👤</div>
                    <div>No customers yet</div>
                    {canEdit && <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => setShowForm(true)}>+ Add your first customer</button>}
                  </td></tr>
                ) : customers.map(c => (
                  <tr key={c.customerId} style={{ cursor: "pointer" }} onClick={() => setSelectedId(c.customerId)}>
                    <td style={{ fontWeight: 600 }}>{c.name}</td>
                    <td>{c.phone || "—"}</td>
                    <td>{c.email || "—"}</td>
                    <td>{c.totalOrders}</td>
                    <td>{c.totalSales}</td>
                    <td style={{ fontWeight: 700, color: "#10B981" }}>{fmt(c.lifetimeValue)}</td>
                    <td style={{ fontSize: 11.5, color: "var(--text-3)" }}>{c.lastActivityAt ? new Date(c.lastActivityAt).toLocaleDateString() : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div style={{ padding: "12px 16px", borderTop: "1px solid var(--border)" }}>
          <Pager page={page} total={total} per={20} onChange={setPage} />
        </div>
      </div>
    </div>
  );
}

export default Customers;
